import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  PinInput,
  PinInputField,
  Text,
} from '@/ui';
import type { AuthFormValues } from './types';

type Props = {
  control: Control<AuthFormValues>;
  email: string;
  errorMessage?: string;
  canResend: boolean;
  timeRemaining: number;
  formatTime: (ms: number) => string;
  isVerifying: boolean;
  onComplete: () => void;
  onResend: () => void;
};

export const EmailCodeStep = ({
  control,
  email,
  errorMessage,
  canResend,
  timeRemaining,
  formatTime,
  isVerifying,
  onComplete,
  onResend,
}: Props): JSX.Element => {
  const { t } = useTranslation('auth');

  return (
    <>
      <Text data-testid="auth-code-sent" fontSize="sm" textAlign="center">
        {t('code_sent_to', { email })}
        <br />
        {t('code_expires')}
      </Text>

      <FormControl isInvalid={!!errorMessage}>
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <HStack
              data-testid="auth-code"
              justify="center"
              spacing="2"
              w="full"
            >
              {/* `otp` sets autoComplete="one-time-code", which earns iOS and
                  Chrome autofill of the emailed code for free. */}
              <PinInput
                autoFocus
                otp
                placeholder=""
                size="lg"
                type="number"
                value={field.value}
                onChange={field.onChange}
                onComplete={(value) => {
                  field.onChange(value);
                  onComplete();
                }}
              >
                {/* Six boxes at their natural size need 328px, more than a
                    360px-wide phone has left after the page gutters, so they
                    give up width instead of running off the screen. */}
                {Array.from({ length: 6 }, (_, index) => (
                  <PinInputField
                    bg="white"
                    flex="1"
                    key={index}
                    maxW="12"
                    ref={index === 0 ? field.ref : undefined}
                  />
                ))}
              </PinInput>
            </HStack>
          )}
          rules={{
            validate: {
              code: (value) =>
                String(value).length === 6 || t('validation.code_incomplete'),
            },
          }}
        />
        <FormErrorMessage justifyContent="center">
          {errorMessage}
        </FormErrorMessage>
      </FormControl>

      <Button data-testid="auth-submit" isLoading={isVerifying} type="submit">
        {t('code_submit')}
      </Button>

      <Box color="customgray.mid" fontSize="sm" textAlign="center">
        {canResend ? (
          <Button
            colorScheme="dark"
            fontSize="sm"
            fontWeight="normal"
            type="button"
            variant="link"
            onClick={onResend}
          >
            {t('code_resend')}
          </Button>
        ) : (
          <Text>
            {t('code_resend_in', { time: formatTime(timeRemaining) })}
          </Text>
        )}
      </Box>
    </>
  );
};
